import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api';

const ACTION_LABELS = {
  remove_post: 'Removed post',
  remove_reply: 'Removed reply',
  warn_user: 'Warned user',
  suspend_user: 'Suspended user',
  ban_request: 'Ban requested',
  ban_execute: 'Ban executed',
  lock_thread: 'Locked thread',
  unlock_thread: 'Unlocked thread',
  pin_resource: 'Pinned resource',
  flag_admin: 'Flagged for admin',
  crisis_auto: 'Crisis detected'
};

const ACTION_COLORS = {
  remove_post: '#f97316',
  remove_reply: '#f97316',
  warn_user: '#e8b547',
  suspend_user: '#f97316',
  ban_request: '#d47e6a',
  ban_execute: '#d47e6a',
  lock_thread: 'rgba(237, 228, 211, 0.38)',
  unlock_thread: 'rgba(237, 228, 211, 0.38)',
  flag_admin: '#c084fc',
  crisis_auto: '#fca5a5'
};

function timeAgo(dateStr) {
  const seconds = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function ModDashboard() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('log');

  const isAdmin = user.role === 'admin';
  const isMod = user.role === 'moderator' || isAdmin;

  useEffect(() => {
    if (!isMod) return;
    const fetches = [api.get('/mod/log')];
    if (isAdmin) fetches.push(api.get('/mod/flags'));

    Promise.all(fetches).then(results => {
      setLogs(results[0].data.logs);
      if (results[1]) setFlags(results[1].data.flags);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [isMod, isAdmin]);

  if (!isMod) return <Navigate to="/" />;

  const tabStyle = (active) => ({
    fontSize: 14, padding: '8px 16px', borderRadius: 9999, cursor: 'pointer',
    fontFamily: '"Geist", "Inter", system-ui, sans-serif',
    border: active ? '1px solid #e8b547' : '1px solid rgba(237,228,211,0.08)',
    background: active ? '#e8b547' : '#120c09',
    color: active ? '#0e0a08' : 'rgba(237, 228, 211, 0.38)',
  });

  return (
    <div style={{ minHeight: '100vh', paddingBottom: 120, background: '#0e0a08' }}>
      <Navbar />

      <div style={{ borderBottom: '1px solid rgba(237,228,211,0.08)', background: 'rgba(18,12,9,0.3)' }}>
        <div style={{ maxWidth: 680, margin: '0 auto', padding: '20px 22px' }}>
          <h2 style={{ fontFamily: '"Instrument Serif", serif', fontSize: 18, color: '#ede4d3', margin: 0 }}>Moderation</h2>
          <p style={{ color: 'rgba(237, 228, 211, 0.25)', fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif', marginTop: 4 }}>
            {isAdmin ? 'Admin view — all actions and flags' : 'Your moderation actions'}
          </p>
        </div>
      </div>

      <main style={{ maxWidth: 680, margin: '0 auto', padding: '24px 22px' }}>
        {/* Tabs */}
        {isAdmin && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button onClick={() => setTab('log')} style={tabStyle(tab === 'log')}>Action Log</button>
            <button onClick={() => setTab('flags')} style={tabStyle(tab === 'flags')}>
              Flags {flags.length > 0 && `(${flags.length})`}
            </button>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p style={{ color: '#e8b547', fontFamily: '"Instrument Serif", serif' }}>Loading...</p>
          </div>
        ) : tab === 'log' ? (
          /* Action log */
          logs.length === 0 ? (
            <div style={{ background: 'rgba(18,12,9,0.3)', border: '1px dashed rgba(237,228,211,0.08)', borderRadius: 16, padding: 40, textAlign: 'center' }}>
              <p style={{ color: 'rgba(237, 228, 211, 0.2)', fontSize: 14, fontFamily: '"Geist", "Inter", system-ui, sans-serif' }}>No moderation actions yet.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {logs.map(log => (
                <div key={log._id} style={{ background: '#120c09', border: '1px solid rgba(237,228,211,0.08)', borderRadius: 12, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontFamily: '"Geist", "Inter", system-ui, sans-serif', fontWeight: 500, color: ACTION_COLORS[log.action] || 'rgba(237, 228, 211, 0.38)' }}>
                          {ACTION_LABELS[log.action] || log.action}
                        </span>
                        {log.targetAlias && (
                          <span style={{ fontSize: 12, color: 'rgba(232,181,71,0.6)', fontFamily: '"Geist", "Inter", system-ui, sans-serif' }}>{log.targetAlias}</span>
                        )}
                        <span style={{ fontSize: 12, color: 'rgba(237, 228, 211, 0.15)', fontFamily: '"Geist", "Inter", system-ui, sans-serif' }}>by {log.moderatorAlias}</span>
                      </div>
                      <p style={{ fontSize: 12, color: 'rgba(237, 228, 211, 0.25)', fontFamily: '"Geist", "Inter", system-ui, sans-serif', marginTop: 4 }}>{log.reason}</p>
                      {log.details && <p style={{ fontSize: 12, color: 'rgba(237, 228, 211, 0.15)', fontFamily: '"Geist", "Inter", system-ui, sans-serif', marginTop: 2 }}>{log.details}</p>}
                    </div>
                    <span style={{ fontSize: 12, color: 'rgba(237, 228, 211, 0.15)', fontFamily: '"Geist", "Inter", system-ui, sans-serif', whiteSpace: 'nowrap' }}>{timeAgo(log.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          /* Flags (admin only) */
          flags.length === 0 ? (
            <div style={{ background: 'rgba(18,12,9,0.3)', border: '1px dashed rgba(237,228,211,0.08)', borderRadius: 16, padding: 40, textAlign: 'center' }}>
              <p style={{ color: 'rgba(237, 228, 211, 0.2)', fontSize: 14, fontFamily: '"Geist", "Inter", system-ui, sans-serif' }}>No pending flags.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {flags.map(flag => (
                <div key={flag._id} style={{ background: '#120c09', border: '1px solid rgba(212,126,106,0.2)', borderRadius: 12, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, color: '#c084fc', fontFamily: '"Geist", "Inter", system-ui, sans-serif', fontWeight: 500 }}>Flag</span>
                        {flag.targetAlias && (
                          <span style={{ fontSize: 12, color: 'rgba(232,181,71,0.6)', fontFamily: '"Geist", "Inter", system-ui, sans-serif' }}>re: {flag.targetAlias}</span>
                        )}
                        <span style={{ fontSize: 12, color: 'rgba(237, 228, 211, 0.15)', fontFamily: '"Geist", "Inter", system-ui, sans-serif' }}>by {flag.moderatorAlias}</span>
                      </div>
                      <p style={{ fontSize: 12, color: 'rgba(237, 228, 211, 0.5)', fontFamily: '"Geist", "Inter", system-ui, sans-serif', marginTop: 4 }}>{flag.reason}</p>
                    </div>
                    <span style={{ fontSize: 12, color: 'rgba(237, 228, 211, 0.15)', fontFamily: '"Geist", "Inter", system-ui, sans-serif', whiteSpace: 'nowrap' }}>{timeAgo(flag.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
}
